import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, LogOut, ChevronDown } from "lucide-react";
import { setSidebarMobileOpen } from "../../utils/appSlice";
import { setUserDetails } from "../../utils/loginSlice";
import { setShowChat } from "../../utils/chatSlice";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useMsal } from "@azure/msal-react";
import { HEADERCONST, STORAGE_KEYS } from "../../utils/constants";
import BEAGLELOGO from "../../static/beagle-logo.svg";
import PropTypes from "prop-types";

const PAGE_TITLES = {
  "/beagle-chat": "Insights",
  "/beagle-chat/chat": "Chat",
  "/beagle-chat/about": "About",
  "/beagle-chat/notification": "Notifications",
  "/beagle-chat/admin": "Admin",
};

const Header = ({ userDetails }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { instance } = useMsal();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const pageTitle = PAGE_TITLES[location.pathname] || null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const logOutUser = async () => {
    try {
      dispatch(setShowChat(false));
      dispatch(setUserDetails({}));

      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        try {
          await instance.logoutPopup({ account: activeAccount });
        } catch (msalErr) {
          console.error("MSAL logoutPopup failed:", msalErr);
        }
        try {
          await instance.clearCache();
        } catch (clearErr) {
          console.error("MSAL clearCache failed:", clearErr);
        }
      }

      localStorage.clear();
      sessionStorage.clear();
      sessionStorage.setItem(STORAGE_KEYS.LOGOUT_FLAG, "true");
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
      localStorage.clear();
      sessionStorage.clear();
      sessionStorage.setItem(STORAGE_KEYS.LOGOUT_FLAG, "true");
      navigate("/login");
    }
  };

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '52px',
        flexShrink: 0,
        padding: '0 16px',
        backgroundColor: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-color)',
        zIndex: 30,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {isMobile && (
          <button
            onClick={() => dispatch(setSidebarMobileOpen(true))}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        )}
        <button
          onClick={() => window.location.reload()}
          style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex' }}
          aria-label="Reload page"
        >
          <img
            src={BEAGLELOGO}
            alt=""
            role="presentation"
            style={{ width: '28px', height: '28px' }}
          />
        </button>
        <h1
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
            lineHeight: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          BeagleGPT
          {pageTitle && (
            <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '6px' }}>
              {pageTitle}
            </span>
          )}
        </h1>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
            aria-label="User menu"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', borderRadius: '8px' }}
            className="hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <div
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                backgroundColor: 'var(--brand-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {userDetails?.email?.[0]?.toUpperCase() || "U"}
            </div>
            {!isMobile && (
              <>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userDetails?.email?.split("@")[0] || "User"}
                </span>
                <ChevronDown size={14} style={{ color: 'var(--text-secondary)', transition: 'transform 0.2s', transform: dropdownOpen ? 'rotate(180deg)' : 'none' }} />
              </>
            )}
          </button>

          {dropdownOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: '4px',
                width: '200px',
                backgroundColor: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                boxShadow: '0 10px 15px -3px var(--shadow-color)',
                padding: '4px 0',
                zIndex: 50,
              }}
            >
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userDetails?.email || "User"}
                </p>
              </div>
              <button
                onClick={() => { setDropdownOpen(false); logOutUser(); }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 12px', fontSize: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                className="text-[var(--text-secondary)] hover:text-red-500 hover:bg-[var(--bg-secondary)] transition-colors"
              >
                <LogOut size={14} />
                {HEADERCONST.LOGOUT}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  userDetails: PropTypes.object,
};

export default Header;
